import { useEffect }             from 'react';
import type { TokaMapInstance }  from '@tokamap/core';
import openImportImageModal      from '@/TokamapEditor/helpers/openImportImageModal';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import toggleRegionTool          from '@/TokamapEditor/helpers/toggleRegionTool';
import buildingPickStore         from '@/TokamapEditor/stores/buildingPickStore';
import toggleOpeningTool         from '@/TokamapEditor/helpers/toggleOpeningTool';
import toggleCutTool             from '@/TokamapEditor/helpers/toggleCutTool';
import { SHOW_PLAN_IMPORT }      from '@/TokamapEditor/ux/constants';
import { SHOW_MARKER_TOOL }      from '@/TokamapEditor/ux/constants';
import { SHOW_DECOR }            from '@/TokamapEditor/ux/constants';
import { mergeShapes }           from '@/TokamapEditor/helpers/mergeShapes';
import leftSidebarStore          from '@/TokamapEditor/stores/leftSidebarStore';
import { openDecorCatalogue }    from '@/TokamapEditor/decor/decorSidebars';

/**
 * Raccourcis clavier globaux : "r" démarre le dessin d'un rectangle, "c" celui d'un cercle, "p"
 * celui d'un polygone, "l" celui d'une ligne, "m" pose un marqueur (mêmes actions que les boutons de `Actions.tsx`, voir
 * `mapAdapter.startDrawingRectangle`/`startDrawingCircle`/`startDrawingPolygon`/`startDrawingLine`),
 * "i" ouvre la modal d'import d'image puis démarre le dessin de l'image importée (voir
 * `openImportImageModal`), "z" arme/range l'outil de zone d'analyse (voir `toggleRegionTool`),
 * "d" ouvre ou referme le catalogue Mobilier & Déco,
 * Suppr/Retour
 * arrière supprime la sélection courante,
 * Ctrl+C/Ctrl+V copient/collent la sélection courante (voir
 * `TokaMapInstance.copyShapes`/`pasteClipboard`), Ctrl+L verrouille la sélection courante (voir
 * `TokaMapInstance.setShapeLocked`), Ctrl+Z/Ctrl+Y annulent/rétablissent (voir
 * `TokaMapInstance.history`). La sélection
 * multiple (`selectionShapeIds`, rectangle de drag — BESOINS.md §13) est prioritaire sur la
 * sélection simple (`selectedShapeId`, clic sur une forme) si les deux sont actives en même temps
 * — rare en pratique (le clic simple n'ouvre la sidebar que hors sélection multiple), mais garde
 * un comportement défini plutôt qu'ambigu. Une forme verrouillée (`Shape.locked`) est exclue de
 * Suppr/Ctrl+C/Ctrl+L : la sélection multiple ne peut déjà plus en contenir (voir
 * `MapLibreAdapter`), mais la sélection simple (clic gauche) le peut encore, d'où ce garde-fou
 * explicite ici (verrouiller une forme déjà verrouillée n'aurait de toute façon aucun effet).
 */
export function useKeyboardShortcuts(
	instance: TokaMapInstance | null,
	selectedShapeId: string | null,
	selectionShapeIds: string[],
) {
	useEffect(() => {
		if (!instance) return;
		// Capturé dans une variable dédiée : TypeScript ne conserve pas le narrowing de `instance`
		// (paramètre du hook) à l'intérieur de `handleKeyDown`, déclarée séparément ci-dessous.
		const activeInstance = instance;

		function isShapeLocked(shapeId: string): boolean {
			return !!activeInstance.shapeStore.getAll().find((shape) => shape.id === shapeId)?.locked;
		}

		function handleKeyDown(event: KeyboardEvent): void {
			// Aucun champ de saisie dans cette app aujourd'hui (Sidebar est en lecture seule), mais
			// reste robuste si un consommateur en ajoute un un jour.
			const target = event.target as HTMLElement | null;
			if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
				return;
			}

			if (event.ctrlKey || event.metaKey) {
				switch (event.key.toLowerCase()) {
					case 'z':
						event.preventDefault();
						if (!currentEditorStore.isDrawing && !buildingPickStore.active) activeInstance.history.undo();
						break;

					case 'y':
						event.preventDefault();
						if (!currentEditorStore.isDrawing && !buildingPickStore.active) activeInstance.history.redo();
						break;

					case 'c': {
						event.preventDefault();
						const idsToCopy = (selectionShapeIds.length > 0 ? selectionShapeIds : selectedShapeId ? [selectedShapeId] : [])
							.filter((shapeId) => !isShapeLocked(shapeId));
						if (idsToCopy.length > 0) activeInstance.copyShapes(idsToCopy);
						break;
					}

					case 'v':
						event.preventDefault();
						activeInstance.pasteClipboard();
						break;

					case 'j':
						event.preventDefault();
						if (selectionShapeIds.length > 1) void mergeShapes(selectionShapeIds);
						break;

					case 'l': {
						event.preventDefault();
						const idsToLock = (selectionShapeIds.length > 0 ? selectionShapeIds : selectedShapeId ? [selectedShapeId] : [])
							.filter((shapeId) => !isShapeLocked(shapeId));
						for (const shapeId of idsToLock) activeInstance.setShapeLocked(shapeId, true);
						break;
					}
				}
				return;
			}

			switch (event.key) {
				case 'r':
				case 'R':
					activeInstance.mapAdapter.startDrawingRectangle();
					break;

				case 'c':
				case 'C':
					activeInstance.mapAdapter.startDrawingCircle();
					break;

				case 'p':
				case 'P':
					activeInstance.mapAdapter.startDrawingPolygon();
					break;

				case 'l':
				case 'L':
					activeInstance.mapAdapter.startDrawingLine();
					break;

				case 'm':
				case 'M':
					if (SHOW_MARKER_TOOL) activeInstance.mapAdapter.startDrawingMarker();
					break;

				case 'i':
				case 'I':
					openImportImageModal();
					break;

				case 'o':
				case 'O':
					toggleOpeningTool(activeInstance);
					break;

				case 'x':
				case 'X':
					toggleCutTool(activeInstance);
					break;

				case 'd':
				case 'D':
					if (!SHOW_DECOR) break;
					if (leftSidebarStore.opened && leftSidebarStore.options.reference === 'decor') leftSidebarStore.close();
					else openDecorCatalogue();
					break;

				case 'w':
				case 'W':
					if (buildingPickStore.active || buildingPickStore.canPick()) buildingPickStore.toggle();
					break;

				// Le "z" NU seulement : Ctrl+Z (annuler) est traité plus haut, qui rend la main avant
				// d'arriver ici.
				case 'z':
				case 'Z':
					if (SHOW_PLAN_IMPORT) toggleRegionTool(activeInstance);
					break;

				case 'Delete':
				case 'Backspace':
					if (selectionShapeIds.length > 0) {
						const idsToDelete = selectionShapeIds.filter((shapeId) => !isShapeLocked(shapeId));
						// `removeFromSelection` reste appelé par forme (état d'affichage de la sélection, sans
						// rapport avec History/onSave) ; `deleteShape` en revanche est appelé UNE SEULE FOIS
						// avec tout le groupe (voir TokaMapInstance.deleteShape) — une seule entrée History/un
						// seul onSave pour toute la suppression, plutôt qu'un appel par forme.
						for (const shapeId of idsToDelete) activeInstance.mapAdapter.removeFromSelection(shapeId);
						if (idsToDelete.length > 0) activeInstance.deleteShape(idsToDelete);
					} else if (selectedShapeId && !isShapeLocked(selectedShapeId)) {
						activeInstance.deleteShape(selectedShapeId);
					}
					break;
			}
		}

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [instance, selectedShapeId, selectionShapeIds]);
}
